import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  accruedInterest,
  LOAN_PAYMENT_KIND_LABELS,
  PAYMENT_FREQUENCY_LABELS,
  RATE_BASIS_LABELS,
  periodStart,
  projectSchedule,
  unshiftedDueDate,
  type Account,
  type AmortRow,
  type BusinessDayRule,
  type LoanPaymentKind,
  type LoanTerms,
  type PaymentFrequency,
  type RateBasis,
} from '@accounting/shared';
import { AccountPicker } from '../accounts/AccountPicker';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import type { LoanSide } from './LoanBuilder';

// Detail page shared by both registers; the page maps its API shape onto
// LoanDetailModel and supplies the side-specific labels and mutation.

export interface LoanDetailPayment {
  id: string;
  paymentDate: string;
  kind: LoanPaymentKind;
  scheduledPeriodNo: number | null;
  dueDate: string | null;
  accruedTo: string | null;
  amount: string;
  principalPortion: string;
  interestPortion: string;
  lateCharge: string;
  balanceAfter: string;
  journalEntryId: string | null;
}

export interface LoanDetailModel {
  id: string;
  name: string;
  status: 'active' | 'paid_off';
  principal: string;
  annualRatePct: string;
  rateBasis: RateBasis;
  paymentFrequency: PaymentFrequency;
  amortizationPeriods: number;
  numberOfPayments: number;
  paymentAmount: string;
  originationDate: string;
  firstPaymentDate: string;
  maturityDate: string;
  businessDayRule: BusinessDayRule;
  lateChargePct: string | null;
  lateChargeGraceDays: number | null;
  defaultRateSpreadPct: string | null;
  currentBalance: string;
  periodsPaid: number;
  nextDueDate: string | null;
  nextDueAmount: string | null;
  projectedBalloon: string;
  isOverdue: boolean;
  autoPostPayments: boolean;
  bankAccountId: string | null;
  originationJournalEntryId: string | null;
  schedule: AmortRow[];
  payments: LoanDetailPayment[];
}

export interface LoanPayInput {
  paymentDate: string;
  kind: LoanPaymentKind;
  amount?: string;
  bankAccountId: string;
  memo?: string;
}

interface Props {
  side: LoanSide;
  loan: LoanDetailModel;
  accounts: Account[];
  onPay: (input: LoanPayInput) => void;
  isPending: boolean;
  errorMessage?: string | null;
  backHref: '/loans' | '/shareholder-loans';
  backLabel: string;
}

const PAY_KINDS: LoanPaymentKind[] = ['scheduled', 'prepayment', 'balloon', 'payoff', 'late_charge'];

function termsOf(loan: LoanDetailModel): LoanTerms {
  return {
    principal: loan.principal,
    annualRatePct: loan.annualRatePct,
    rateBasis: loan.rateBasis,
    paymentFrequency: loan.paymentFrequency,
    amortizationPeriods: loan.amortizationPeriods,
    numberOfPayments: loan.numberOfPayments,
    paymentAmount: loan.paymentAmount,
    originationDate: loan.originationDate,
    firstPaymentDate: loan.firstPaymentDate,
    businessDayRule: loan.businessDayRule,
  };
}

const later = (a: string | null, b: string) => (a && a > b ? a : b);

export function LoanDetailView({ side, loan, accounts, onPay, isPending, errorMessage, backHref, backLabel }: Props) {
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [kind, setKind] = useState<LoanPaymentKind>('scheduled');
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState(loan.bankAccountId ?? '');
  const [showOriginal, setShowOriginal] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  const isCashSelectable = (a: Account) => a.isActive && a.allowDirectPosting && a.accountType === 'asset';
  const terms = useMemo(() => termsOf(loan), [loan]);
  const lastAccruedTo = loan.payments.length ? loan.payments[loan.payments.length - 1]!.accruedTo : null;
  const next = loan.schedule[0] ?? null;

  // What this payment will do, computed with the same engine the server uses.
  const preview = useMemo(() => {
    const bal = parseFloat(loan.currentBalance);
    const typed = amount.trim() ? parseFloat(amount) || 0 : null;
    switch (kind) {
      case 'scheduled': {
        if (!next || next.kind !== 'scheduled') return { note: 'Every scheduled payment has been made — record the balloon instead.', amount: null as string | null, interest: null as string | null, principal: null as string | null };
        const interest = parseFloat(next.interest);
        const paying = typed ?? parseFloat(next.payment);
        const principal = Math.min(Math.max(0, paying - interest), bal);
        const extra = paying - parseFloat(next.payment);
        return {
          note: `Installment ${next.periodNo} of ${loan.numberOfPayments}, due ${formatAccountingDate(next.dueDate)}${extra > 0.004 ? ` — ${formatCurrency(String(extra))} extra goes to principal` : ''}`,
          amount: paying.toFixed(2), interest: interest.toFixed(2), principal: principal.toFixed(2),
        };
      }
      case 'prepayment': {
        const from = later(lastAccruedTo, periodStart(terms, loan.periodsPaid + 1));
        const interest = parseFloat(accruedInterest(terms, loan.currentBalance, from, paymentDate));
        const paying = typed ?? 0;
        return {
          note: `Interest accrued ${formatAccountingDate(from)} → ${formatAccountingDate(paymentDate)} at the daily rate; the rest reduces principal and the balloon. The payment amount does not change.`,
          amount: paying ? paying.toFixed(2) : null, interest: interest.toFixed(2), principal: paying ? Math.min(Math.max(0, paying - interest), bal).toFixed(2) : null,
        };
      }
      case 'balloon':
      case 'payoff': {
        const from = kind === 'balloon'
          ? later(lastAccruedTo, unshiftedDueDate(terms, loan.numberOfPayments))
          : later(lastAccruedTo, periodStart(terms, loan.periodsPaid + 1));
        const interest = parseFloat(accruedInterest(terms, loan.currentBalance, from, paymentDate));
        const owed = bal + interest;
        const remaining = loan.numberOfPayments - loan.periodsPaid;
        return {
          note: kind === 'balloon' && remaining > 0
            ? `${remaining} scheduled payment(s) remain — record those first, or record a payoff.`
            : `Settles the balance in full: ${formatCurrency(loan.currentBalance)} principal + ${formatCurrency(String(interest))} interest.`,
          amount: owed.toFixed(2), interest: interest.toFixed(2), principal: bal.toFixed(2),
        };
      }
      case 'late_charge': {
        const pct = loan.lateChargePct ? parseFloat(loan.lateChargePct) : null;
        const dflt = pct !== null ? +(parseFloat(loan.paymentAmount) * pct / 100).toFixed(2) : null;
        const charge = typed ?? dflt;
        return {
          note: pct !== null ? `${pct}% of the ${formatCurrency(loan.paymentAmount)} installment. Does not change the balance.` : 'Enter the charge. Does not change the balance.',
          amount: charge !== null ? charge.toFixed(2) : null, interest: null, principal: null,
        };
      }
    }
  }, [kind, amount, paymentDate, loan, next, terms, lastAccruedTo]);

  const amountLocked = kind === 'balloon' || kind === 'payoff';
  const canPay =
    loan.status === 'active' && !!bankAccountId && !isPending &&
    (kind === 'scheduled' ? !!next && next.kind === 'scheduled' : true) &&
    (kind === 'prepayment' || (kind === 'late_charge' && !loan.lateChargePct) ? !!amount.trim() : true) &&
    (kind !== 'balloon' || loan.periodsPaid >= loan.numberOfPayments);

  const submit = () => {
    onPay({
      paymentDate,
      kind,
      amount: amountLocked ? undefined : amount.trim() ? String(parseFloat(amount) || 0) : undefined,
      bankAccountId,
    });
    setAmount('');
  };

  const originalSchedule = useMemo(() => (showOriginal ? projectSchedule(terms) : []), [showOriginal, terms]);
  const rows = showOriginal ? originalSchedule : loan.schedule;
  const visible = showAllRows ? rows : rows.slice(0, 60);
  const balloonRow = rows.find((r) => r.kind === 'balloon');

  // Per-calendar-year statement (what the other party reports on their return).
  const years = useMemo(() => {
    const map = new Map<string, { interest: number; principal: number; lateCharges: number; count: number; ending: string }>();
    for (const p of loan.payments) {
      const y = p.paymentDate.slice(0, 4);
      const cur = map.get(y) ?? { interest: 0, principal: 0, lateCharges: 0, count: 0, ending: p.balanceAfter };
      cur.interest += parseFloat(p.interestPortion);
      cur.principal += parseFloat(p.principalPortion);
      cur.lateCharges += parseFloat(p.lateCharge);
      cur.count += 1;
      cur.ending = p.balanceAfter;
      map.set(y, cur);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [loan.payments]);

  const freqWord = PAYMENT_FREQUENCY_LABELS[loan.paymentFrequency].toLowerCase();
  const defaultRate = loan.defaultRateSpreadPct ? (parseFloat(loan.annualRatePct) + parseFloat(loan.defaultRateSpreadPct)).toFixed(2) : null;

  return (
    <div>
      <Link to={backHref} style={{ fontSize: 13 }}>← {backLabel}</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {loan.name}
        {loan.status === 'paid_off' ? <span className="tag">paid off</span> : <span className="tag ok">active</span>}
        {loan.isOverdue && <span className="tag bad">overdue</span>}
        {loan.autoPostPayments && loan.status === 'active' && <span className="tag info">auto-post</span>}
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16, maxWidth: 1000 }}>
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 14, marginTop: 0 }}>Balance</h2>
          <div className="form-grid" style={{ marginBottom: 0 }}>
            <span className="muted">Current balance</span><strong style={{ fontSize: 18 }}>{formatCurrency(loan.currentBalance)}</strong>
            <span className="muted">Next due</span>
            <span>{loan.nextDueDate ? `${formatAccountingDate(loan.nextDueDate)} · ${formatCurrency(loan.nextDueAmount ?? '0')}` : '—'}</span>
            <span className="muted">Payments made</span><span>{loan.periodsPaid} of {loan.numberOfPayments}</span>
            <span className="muted">Balloon at maturity</span>
            <span>{Number(loan.projectedBalloon) > 0 ? `${formatCurrency(loan.projectedBalloon)} on ${formatAccountingDate(loan.maturityDate)}` : 'none'}</span>
            {loan.isOverdue && defaultRate && (
              <>
                <span className="muted">While in default</span>
                <span className="field-error">{defaultRate}% ({loan.annualRatePct}% + {loan.defaultRateSpreadPct}%) — informational, not posted</span>
              </>
            )}
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 14, marginTop: 0 }}>Terms</h2>
          <div className="form-grid" style={{ marginBottom: 0 }}>
            <span className="muted">Principal</span><span>{formatCurrency(loan.principal)} from {formatAccountingDate(loan.originationDate)}</span>
            <span className="muted">Rate</span><span>{Number(loan.annualRatePct)}% · {RATE_BASIS_LABELS[loan.rateBasis]}</span>
            <span className="muted">Payment</span>
            <span>{formatCurrency(loan.paymentAmount)} {freqWord}, {loan.numberOfPayments} payments on a {loan.amortizationPeriods}-payment amortization</span>
            <span className="muted">First / last</span><span>{formatAccountingDate(loan.firstPaymentDate)} → {formatAccountingDate(loan.maturityDate)}</span>
            <span className="muted">Due dates</span><span>{loan.businessDayRule === 'next_business_day' ? 'next business day when on a weekend or holiday' : 'as scheduled'}</span>
            {loan.lateChargePct && (
              <>
                <span className="muted">Late charge</span>
                <span>{Number(loan.lateChargePct)}% after {loan.lateChargeGraceDays ?? 0} days{defaultRate ? ` · default rate ${defaultRate}%` : ''}</span>
              </>
            )}
            {loan.originationJournalEntryId && (
              <>
                <span className="muted">Origination entry</span>
                <Link to="/journal/$id" params={{ id: loan.originationJournalEntryId }}>view</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {loan.status === 'active' && (
        <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 1000 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>{side === 'payable' ? 'Record payment' : 'Record repayment'}</strong>
            <select value={kind} onChange={(e) => { setKind(e.target.value as LoanPaymentKind); setAmount(''); }} aria-label="Payment kind">
              {PAY_KINDS.map((k) => <option key={k} value={k}>{LOAN_PAYMENT_KIND_LABELS[k]}</option>)}
            </select>
            <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            <input
              inputMode="decimal"
              value={amountLocked ? (preview.amount ?? '') : amount}
              disabled={amountLocked}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder={preview.amount ? `${preview.amount}${kind === 'scheduled' ? ' (scheduled)' : ''}` : '0.00'}
              style={{ width: 170, textAlign: 'right' }}
              aria-label="Amount"
            />
            <div style={{ minWidth: 260 }}>
              <AccountPicker accounts={accounts} value={bankAccountId} onChange={setBankAccountId} predicate={isCashSelectable} placeholder={side === 'payable' ? '— pay from —' : '— receive into —'} />
            </div>
            <button className="btn-primary" disabled={!canPay} onClick={submit}>
              {isPending ? 'Posting…' : 'Post'}
            </button>
            <div className="muted" style={{ flexBasis: '100%', fontSize: 13 }}>
              {preview.note}
              {preview.amount && preview.interest !== null && preview.principal !== null && (
                <> · <strong>{formatCurrency(preview.amount)}</strong> = interest {formatCurrency(preview.interest)} + principal {formatCurrency(preview.principal)}</>
              )}
            </div>
            {errorMessage && <span className="field-error">{errorMessage}</span>}
          </div>
        </div>
      )}

      {loan.payments.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 1000 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Payments made</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Kind</th>
                <th>Installment</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Principal</th>
                <th style={{ textAlign: 'right' }}>Interest</th>
                <th style={{ textAlign: 'right' }}>Late charge</th>
                <th style={{ textAlign: 'right' }}>Balance after</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loan.payments.map((p) => (
                <tr key={p.id}>
                  <td>{formatAccountingDate(p.paymentDate)}</td>
                  <td>{LOAN_PAYMENT_KIND_LABELS[p.kind]}</td>
                  <td>{p.scheduledPeriodNo ? `${p.scheduledPeriodNo} of ${loan.numberOfPayments}${p.dueDate && p.dueDate !== p.paymentDate ? ` (due ${formatAccountingDate(p.dueDate)})` : ''}` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.principalPortion)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.interestPortion)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(p.lateCharge) > 0 ? formatCurrency(p.lateCharge) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.balanceAfter)}</td>
                  <td>{p.journalEntryId && <Link to="/journal/$id" params={{ id: p.journalEntryId }} style={{ fontSize: 12 }}>entry</Link>}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 style={{ fontSize: 13, margin: '16px 0 6px' }}>By calendar year</h3>
          <table className="table" style={{ maxWidth: 640 }}>
            <thead>
              <tr>
                <th>Year</th>
                <th style={{ textAlign: 'right' }}>Payments</th>
                <th style={{ textAlign: 'right' }}>Interest</th>
                <th style={{ textAlign: 'right' }}>Principal</th>
                <th style={{ textAlign: 'right' }}>Late charges</th>
                <th style={{ textAlign: 'right' }}>Ending balance</th>
              </tr>
            </thead>
            <tbody>
              {years.map(([y, v]) => (
                <tr key={y}>
                  <td>{y}</td>
                  <td style={{ textAlign: 'right' }}>{v.count}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(String(v.interest))}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(String(v.principal))}</td>
                  <td style={{ textAlign: 'right' }}>{v.lateCharges > 0 ? formatCurrency(String(v.lateCharges)) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(v.ending)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{ padding: 16, maxWidth: 1000 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{showOriginal ? 'Original schedule (at origination)' : 'Remaining schedule (from the live balance)'}</h2>
          <button type="button" className="btn-secondary" onClick={() => { setShowOriginal((v) => !v); setShowAllRows(false); }}>
            {showOriginal ? 'Show remaining' : 'Show original'}
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="muted">Nothing left to pay.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Due</th>
                <th style={{ textAlign: 'right' }}>Payment</th>
                <th style={{ textAlign: 'right' }}>Principal</th>
                <th style={{ textAlign: 'right' }}>Interest</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {visible.filter((r) => r.kind === 'scheduled').map((r) => (
                <tr key={r.periodNo} style={{ opacity: showOriginal && r.periodNo <= loan.periodsPaid ? 0.55 : 1 }}>
                  <td>{r.periodNo}</td>
                  <td>{formatAccountingDate(r.dueDate)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.payment)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.principal)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.interest)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.balance)}</td>
                </tr>
              ))}
              {!showAllRows && rows.length > visible.length && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center' }}>
                    <button type="button" className="btn-secondary" onClick={() => setShowAllRows(true)}>
                      Show all {rows.filter((r) => r.kind === 'scheduled').length} payments
                    </button>
                  </td>
                </tr>
              )}
              {balloonRow && (
                <tr style={{ fontWeight: 600 }}>
                  <td>Balloon</td>
                  <td>{formatAccountingDate(balloonRow.dueDate)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(balloonRow.payment)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(balloonRow.principal)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency('0')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency('0')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
